/**
 * M11: the ship illustration system. ONE geometry definition per ship class, drawn by
 *   - React/SVG   (graphics/ShipArt.tsx: fleet menu, system panel, previews)
 *   - PixiJS      (graphics/pixi-ships.ts: galaxy map markers, battle scenes)
 * so a corvette looks like the same corvette everywhere.
 *
 * Pure data + pure helpers (no PixiJS, no React): unit-tested for determinism and symmetry.
 *
 * Coordinates: the ship points UP (nose at y = -1), tail at y ≈ +0.9, x is the ship's right.
 * The art box is ART_BOX (engine flames use the room below the tail). Visual only: ship data
 * (hull, shield, counts) always comes from the server and is never derived from the art.
 */
import { ROLE_COLORS, mixHex, type Role } from '../design/tokens.ts';

export type ShipClass = 'scout' | 'construction' | 'corvette' | 'frigate' | 'cruiser';
export const SHIP_CLASSES: ShipClass[] = ['scout', 'construction', 'corvette', 'frigate', 'cruiser'];

export type Tone = 'hull' | 'hullLight' | 'hullDark' | 'accent' | 'glass' | 'metal' | 'weapon' | 'panel';

export type Layer =
  | { kind: 'poly'; pts: number[]; fill: Tone; stroke?: Tone; alpha?: number }
  | { kind: 'circle'; cx: number; cy: number; r: number; fill: Tone; stroke?: Tone }
  | { kind: 'ellipse'; cx: number; cy: number; rx: number; ry: number; fill: Tone }
  | { kind: 'line'; pts: number[]; stroke: Tone; width: number };

export interface Nozzle { x: number; y: number; w: number }

export interface ShipDesign {
  cls: ShipClass;
  /** Relative on-screen size (cruiser is the biggest) */
  sizeFactor: number;
  layers: Layer[];
  nozzles: Nozzle[];
  /** Flame length relative to the art box, and its colour family */
  flame: { length: number; color: number; core: number };
  /** Fixed positions for damage marks (scorch, cracks, embers) */
  damageSpots: Array<{ x: number; y: number; r: number }>;
  label: string;
}

/** The drawing area: x ∈ [-1.2, 1.2], y ∈ [-1.3, 1.3] */
export const ART_BOX = { x: -1.2, y: -1.3, w: 2.4, h: 2.6 } as const;

// ---- geometry helpers ----

/** Full outline from the right half (x ≥ 0, top → bottom): mirrors it onto the left. */
export function mirrorOutline(rightHalf: Array<[number, number]>): number[] {
  const out: number[] = [];
  for (const [x, y] of rightHalf) out.push(x, y);
  for (let i = rightHalf.length - 1; i >= 0; i--) {
    const [x, y] = rightHalf[i]!;
    if (x !== 0) out.push(-x, y);
  }
  return out;
}

/** The same polygon reflected across the centre line. */
export function mirrorPoly(pts: number[]): number[] {
  const out: number[] = [];
  for (let i = pts.length - 2; i >= 0; i -= 2) out.push(-pts[i]!, pts[i + 1]!);
  return out;
}

const both = (pts: number[], fill: Tone, stroke?: Tone, alpha?: number): Layer[] => [
  { kind: 'poly', pts, fill, stroke, alpha },
  { kind: 'poly', pts: mirrorPoly(pts), fill, stroke, alpha },
];

// ---- the five designs ----

const scout: ShipDesign = {
  cls: 'scout', sizeFactor: 0.85, label: 'Kutatóhajó',
  layers: [
    // swept sensor wings
    ...both([0.12, -0.1, 0.66, 0.34, 0.6, 0.52, 0.16, 0.4], 'hullLight', 'hullDark'),
    // sensor tips
    { kind: 'circle', cx: 0.64, cy: 0.44, r: 0.07, fill: 'accent' },
    { kind: 'circle', cx: -0.64, cy: 0.44, r: 0.07, fill: 'accent' },
    // slim fuselage
    { kind: 'poly', pts: mirrorOutline([[0, -1], [0.09, -0.72], [0.13, -0.2], [0.17, 0.32], [0.19, 0.62], [0.11, 0.85], [0, 0.9]]), fill: 'hull', stroke: 'hullDark' },
    { kind: 'line', pts: [0, -1, 0, -1.2], stroke: 'metal', width: 0.025 },
    { kind: 'circle', cx: 0, cy: -1.2, r: 0.04, fill: 'accent' },
    { kind: 'line', pts: [0, -0.6, 0, 0.55], stroke: 'accent', width: 0.035 },
    { kind: 'ellipse', cx: 0, cy: -0.42, rx: 0.055, ry: 0.15, fill: 'glass' },
    { kind: 'poly', pts: [-0.08, 0.84, 0.08, 0.84, 0.06, 0.95, -0.06, 0.95], fill: 'metal' },
  ],
  nozzles: [{ x: 0, y: 0.95, w: 0.1 }],
  flame: { length: 0.55, color: 0x7dd3fc, core: 0xe0f7ff },
  damageSpots: [{ x: 0.02, y: 0.1, r: 0.12 }, { x: -0.4, y: 0.4, r: 0.1 }, { x: 0.1, y: -0.3, r: 0.08 }],
};

const construction: ShipDesign = {
  cls: 'construction', sizeFactor: 1.0, label: 'Építőhajó',
  layers: [
    // cargo pods + struts
    { kind: 'line', pts: [0.2, -0.2, 0.34, -0.2], stroke: 'metal', width: 0.06 },
    { kind: 'line', pts: [0.2, 0.3, 0.34, 0.3], stroke: 'metal', width: 0.06 },
    { kind: 'line', pts: [-0.2, -0.2, -0.34, -0.2], stroke: 'metal', width: 0.06 },
    { kind: 'line', pts: [-0.2, 0.3, -0.34, 0.3], stroke: 'metal', width: 0.06 },
    ...both([0.32, -0.46, 0.66, -0.4, 0.68, 0.52, 0.34, 0.58], 'hullDark', 'metal'),
    ...both([0.36, -0.34, 0.62, -0.3, 0.62, -0.1, 0.36, -0.14], 'panel'),
    ...both([0.36, 0.12, 0.64, 0.14, 0.64, 0.4, 0.36, 0.42], 'panel'),
    // industrial spine
    { kind: 'poly', pts: [-0.2, -0.78, 0.2, -0.78, 0.27, 0.62, -0.27, 0.62], fill: 'hull', stroke: 'hullDark' },
    { kind: 'poly', pts: [-0.28, 0.6, 0.28, 0.6, 0.22, 0.86, -0.22, 0.86], fill: 'hullDark', stroke: 'metal' },
    // work arm with hazard-coloured tip
    { kind: 'poly', pts: [0.16, -0.78, 0.5, -1.02, 0.58, -0.95, 0.24, -0.7], fill: 'metal', stroke: 'hullDark' },
    { kind: 'poly', pts: [0.5, -1.02, 0.58, -0.95, 0.62, -1.0, 0.54, -1.07], fill: 'accent' },
    { kind: 'poly', pts: [-0.1, -0.7, 0.1, -0.7, 0.1, -0.56, -0.1, -0.56], fill: 'glass' },
    { kind: 'line', pts: [0, -0.5, 0, 0.5], stroke: 'accent', width: 0.04 },
    { kind: 'circle', cx: 0, cy: 0.1, r: 0.09, fill: 'accent', stroke: 'hullDark' },
  ],
  nozzles: [{ x: -0.12, y: 0.86, w: 0.14 }, { x: 0.12, y: 0.86, w: 0.14 }],
  flame: { length: 0.5, color: 0xffb347, core: 0xfff3d1 },
  damageSpots: [{ x: 0.5, y: 0.1, r: 0.14 }, { x: -0.1, y: 0.3, r: 0.12 }, { x: -0.5, y: -0.1, r: 0.1 }],
};

const corvette: ShipDesign = {
  cls: 'corvette', sizeFactor: 0.8, label: 'Korvett',
  layers: [
    // forward-swept attack wings with gun pods
    ...both([0.13, -0.24, 0.8, 0.5, 0.72, 0.72, 0.2, 0.46], 'hullLight', 'hullDark'),
    ...both([0.66, 0.34, 0.74, 0.34, 0.74, 0.66, 0.66, 0.66], 'weapon'),
    { kind: 'line', pts: [0.7, 0.3, 0.7, 0.05], stroke: 'metal', width: 0.04 },
    { kind: 'line', pts: [-0.7, 0.3, -0.7, 0.05], stroke: 'metal', width: 0.04 },
    { kind: 'poly', pts: mirrorOutline([[0, -1], [0.1, -0.62], [0.17, -0.1], [0.2, 0.4], [0.15, 0.72], [0.08, 0.88], [0, 0.84]]), fill: 'hull', stroke: 'hullDark' },
    { kind: 'poly', pts: [0.13, -0.24, 0.8, 0.5, 0.76, 0.54, 0.16, -0.12], fill: 'accent', alpha: 0.9 },
    { kind: 'poly', pts: [-0.13, -0.24, -0.8, 0.5, -0.76, 0.54, -0.16, -0.12], fill: 'accent', alpha: 0.9 },
    { kind: 'ellipse', cx: 0, cy: -0.36, rx: 0.07, ry: 0.2, fill: 'glass' },
    { kind: 'poly', pts: [-0.14, 0.8, -0.04, 0.8, -0.05, 0.94, -0.13, 0.94], fill: 'metal' },
    { kind: 'poly', pts: [0.04, 0.8, 0.14, 0.8, 0.13, 0.94, 0.05, 0.94], fill: 'metal' },
  ],
  nozzles: [{ x: -0.09, y: 0.94, w: 0.09 }, { x: 0.09, y: 0.94, w: 0.09 }],
  flame: { length: 0.6, color: 0xff8a50, core: 0xfff0d6 },
  damageSpots: [{ x: 0.5, y: 0.45, r: 0.12 }, { x: 0, y: 0.2, r: 0.1 }, { x: -0.45, y: 0.4, r: 0.1 }],
};

const frigate: ShipDesign = {
  cls: 'frigate', sizeFactor: 1.1, label: 'Fregatt',
  layers: [
    // armour sponsons
    ...both([0.2, -0.42, 0.52, -0.12, 0.56, 0.5, 0.3, 0.66], 'hullLight', 'hullDark'),
    ...both([0.26, -0.3, 0.46, -0.08, 0.5, 0.18, 0.3, 0.1], 'panel'),
    { kind: 'poly', pts: mirrorOutline([[0, -1], [0.13, -0.78], [0.19, -0.3], [0.25, 0.4], [0.29, 0.76], [0.17, 0.94], [0, 0.9]]), fill: 'hull', stroke: 'hullDark' },
    { kind: 'line', pts: [0.3, -0.05, 0.3, 0.55], stroke: 'accent', width: 0.045 },
    { kind: 'line', pts: [-0.3, -0.05, -0.3, 0.55], stroke: 'accent', width: 0.045 },
    // turrets with barrels
    { kind: 'line', pts: [0, -0.36, 0, -0.62], stroke: 'weapon', width: 0.05 },
    { kind: 'circle', cx: 0, cy: -0.34, r: 0.1, fill: 'metal', stroke: 'hullDark' },
    { kind: 'line', pts: [0, 0.14, 0, -0.1], stroke: 'weapon', width: 0.05 },
    { kind: 'circle', cx: 0, cy: 0.16, r: 0.11, fill: 'metal', stroke: 'hullDark' },
    // bridge
    { kind: 'poly', pts: [-0.09, 0.3, 0.09, 0.3, 0.11, 0.56, -0.11, 0.56], fill: 'hullDark', stroke: 'metal' },
    { kind: 'poly', pts: [-0.06, 0.34, 0.06, 0.34, 0.06, 0.4, -0.06, 0.4], fill: 'glass' },
    { kind: 'poly', pts: [-0.2, 0.86, 0.2, 0.86, 0.16, 0.98, -0.16, 0.98], fill: 'metal' },
  ],
  nozzles: [{ x: -0.15, y: 0.98, w: 0.1 }, { x: 0, y: 0.98, w: 0.1 }, { x: 0.15, y: 0.98, w: 0.1 }],
  flame: { length: 0.5, color: 0xff7a45, core: 0xffeacc },
  damageSpots: [{ x: 0.4, y: 0.2, r: 0.14 }, { x: -0.1, y: -0.2, r: 0.1 }, { x: -0.4, y: 0.4, r: 0.12 }],
};

const cruiser: ShipDesign = {
  cls: 'cruiser', sizeFactor: 1.45, label: 'Cirkáló',
  layers: [
    // heavy armour wings, two layers
    ...both([0.2, -0.56, 0.7, -0.16, 0.84, 0.42, 0.74, 0.8, 0.34, 0.62], 'hullLight', 'hullDark'),
    ...both([0.44, -0.2, 0.66, 0.08, 0.72, 0.5, 0.5, 0.56], 'hullDark', 'metal'),
    ...both([0.3, -0.36, 0.56, -0.12, 0.6, 0.0, 0.32, -0.04], 'panel'),
    { kind: 'poly', pts: mirrorOutline([[0, -1], [0.12, -0.86], [0.2, -0.4], [0.3, 0.3], [0.35, 0.76], [0.2, 0.96], [0, 0.93]]), fill: 'hull', stroke: 'hullDark' },
    { kind: 'line', pts: [0.8, 0.0, 0.8, 0.6], stroke: 'accent', width: 0.05 },
    { kind: 'line', pts: [-0.8, 0.0, -0.8, 0.6], stroke: 'accent', width: 0.05 },
    { kind: 'line', pts: [0, -0.9, 0, 0.35], stroke: 'accent', width: 0.04 },
    // four heavy turrets
    { kind: 'line', pts: [0, -0.5, 0, -0.78], stroke: 'weapon', width: 0.06 },
    { kind: 'circle', cx: 0, cy: -0.48, r: 0.11, fill: 'metal', stroke: 'hullDark' },
    { kind: 'line', pts: [0.52, -0.02, 0.52, -0.28], stroke: 'weapon', width: 0.055 },
    { kind: 'circle', cx: 0.52, cy: 0.0, r: 0.1, fill: 'metal', stroke: 'hullDark' },
    { kind: 'line', pts: [-0.52, -0.02, -0.52, -0.28], stroke: 'weapon', width: 0.055 },
    { kind: 'circle', cx: -0.52, cy: 0.0, r: 0.1, fill: 'metal', stroke: 'hullDark' },
    { kind: 'line', pts: [0, 0.3, 0, 0.06], stroke: 'weapon', width: 0.055 },
    { kind: 'circle', cx: 0, cy: 0.32, r: 0.1, fill: 'metal', stroke: 'hullDark' },
    // command tower
    { kind: 'poly', pts: [-0.12, 0.48, 0.12, 0.48, 0.16, 0.8, -0.16, 0.8], fill: 'hullDark', stroke: 'metal' },
    { kind: 'poly', pts: [-0.08, 0.54, 0.08, 0.54, 0.08, 0.62, -0.08, 0.62], fill: 'glass' },
    { kind: 'poly', pts: [-0.3, 0.88, 0.3, 0.88, 0.24, 1.0, -0.24, 1.0], fill: 'metal' },
  ],
  nozzles: [{ x: -0.26, y: 1.0, w: 0.1 }, { x: -0.09, y: 1.0, w: 0.1 }, { x: 0.09, y: 1.0, w: 0.1 }, { x: 0.26, y: 1.0, w: 0.1 }],
  flame: { length: 0.42, color: 0xff6a3d, core: 0xffe5c2 },
  damageSpots: [{ x: 0.6, y: 0.3, r: 0.16 }, { x: -0.55, y: 0.2, r: 0.14 }, { x: 0.1, y: -0.2, r: 0.12 }, { x: -0.1, y: 0.6, r: 0.12 }],
};

export const SHIP_DESIGNS: Record<ShipClass, ShipDesign> = { scout, construction, corvette, frigate, cruiser };

/** Server ship classes are a closed set; anything unexpected is drawn as the most generic hull. */
export function designOf(cls: string): ShipDesign {
  return (SHIP_DESIGNS as Record<string, ShipDesign>)[cls] ?? SHIP_DESIGNS.corvette;
}

// ---- palette ----

export interface ShipPalette {
  hull: number; hullLight: number; hullDark: number; accent: number; glass: number; metal: number; weapon: number; panel: number;
  /** damage marks */
  burn: number; ember: number;
}

/** Hull greys tinted by the role colour; accents carry the role colour itself (readable at any size). */
export function shipPalette(role: Role): ShipPalette {
  const c = ROLE_COLORS[role];
  return {
    hull: mixHex(0x64748b, c.hex, 0.14),
    hullLight: mixHex(0x94a3b8, c.hex, 0.22),
    hullDark: mixHex(0x1e293b, c.darkHex, 0.35),
    accent: c.hex,
    glass: 0xbfe9ff,
    metal: 0x475569,
    weapon: 0x94a3b8,
    panel: mixHex(0x334155, c.darkHex, 0.3),
    burn: 0x0b0f19,
    ember: 0xff8a3c,
  };
}

export const toneColor = (p: ShipPalette, t: Tone): number => p[t];

/** Role of a ship class when it is drawn on its own (fleet category colour code). */
export const SHIP_ROLE: Record<ShipClass, Role> = {
  scout: 'survey', construction: 'build', corvette: 'military', frigate: 'military', cruiser: 'military',
};

/** The class that represents a fleet: the biggest ship wins (cruiser > frigate > corvette > construction > scout). */
export const REPRESENTATIVE_ORDER: ShipClass[] = ['cruiser', 'frigate', 'corvette', 'construction', 'scout'];
export function representativeClass(shipCounts: Record<string, number>): ShipClass | null {
  for (const c of REPRESENTATIVE_ORDER) if ((shipCounts[c] ?? 0) > 0) return c;
  return null;
}

/** 0..1 damage from hull points (never from art). */
export const damageFraction = (current: number, max: number): number => (max > 0 ? Math.min(1, Math.max(0, 1 - current / max)) : 0);

/** How many of the fixed damage spots are visible at a given damage level (0 at ≤ 0.2). */
export function visibleDamageSpots(damage: number, total: number): number {
  if (damage <= 0.2) return 0;
  return Math.min(total, Math.ceil(((damage - 0.2) / 0.8) * total));
}
